import {getRuinsIncomeAnimationDurationMs, RUINS_INCOME_ANIMATION} from '../configs/ruinsIncomeAnimation';
import {INCOME_MARKER} from '../configs/incomeMarker';
import {INCOME_TYPE} from '../constants/income';
import {GAME_TYPES} from '../constants/game';
import {FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER} from '../configs/playbackSpeed';
import {finishRuinsIncomeAnimation, startRuinsIncomeAnimation} from '../redux/actions';
import {selectGameType} from '../redux/reducers/sharedStateReducer/selectors';
import {checkIfReplayRewinding, getReplayPlaybackSpeedMultiplier} from '../utils/replay/replayPlaybackSpeed';
import {markAwaitedAnimationFinished, markAwaitedAnimationPlaying} from '../utils/replay/replayAwaitedAnimations';
import {BOT_ACTION_INTERVAL_MS} from '../utils/bots/botConfig';
import {getBotActionIntervalMs} from '../utils/bots/botActionPace';
import {checkIfPageHidden} from '../utils/sound/checkIfSoundOutputAsleep';
import {shakeFieldCamera} from '../utils/effects/shakeFieldCamera';
import {
  buildRuinsIncomeRebirthCastPlan,
  buildRuinsIncomeStrikeCastPlan,
  buildRuinsIncomeWaveCastPlan,
} from '../utils/effects/buildAbilityCast';
import {createIncomeMarker} from './incomeMarker';
import {easeInOutSine, interpolateCameraFrame} from './cityCaptureAnimationPlan';
import {
  buildRuinsIncomeChanges,
  calcRuinsIncomeCameraFrame,
  calcRuinsIncomePlateSizePx,
  calcRuinsIncomeWaveDelays,
  checkIfSuperRuins,
  placeRuinsIncomeMedallions,
} from './ruinsIncomeAnimationPlan';

// Анимация оборвана (сторож, уничтожение сцены): ожидающие шаги сценария
// отклоняются этим значением, и сценарий молча выходит
const CANCELLED = Symbol('ruins-income-animation-cancelled');

// Ключ среди анимаций, которых ждет проигрыватель записи матча
const AWAITED_ANIMATION_KEY = 'ruins-income';

// Затемнение — поверх всех слоев ячейки,
// вспышки — над ним (ABILITY_CAST), медальоны — над вспышками
const DIM_DEPTH = 8;
const BADGE_DEPTH = 8.35;

// Вспышка руин — в долях стороны медальона: на отдаленном кадре ячейка мала
const EFFECT_SIZE_SHARE_OF_PLATE = 0.62;

const WHITE = 0xffffff;

// Двузначный доход мельче, чтобы не заходить на значок очков
const TWO_DIGITS_FROM = 10;
const TWO_DIGITS_FONT_SIZE = '50px';

const LINEAR_EASE = 'Linear';
const POP_EASE = 'Back.easeOut';
const GROW_EASE = 'Cubic.easeOut';
const SETTLE_EASE = 'Sine.easeInOut';

/**
 * РОСТ ДОХОДА РУИН НА ПОЛЕ — вместо модалки «Доход руин увеличен». Показывается в
 * первом раунде окна увеличенного дохода, когда закрыты итоги раунда и все, что
 * перед ними в очереди модалок.
 *
 * Камера отъезжает, пока все поле по вертикали не встанет в кадр, поле темнеет,
 * рядом с каждыми руинами появляется медальон с прежним настоящим доходом. От
 * центра поля идет волна света; где она проходит, на руины сходит столб света, они
 * вспыхивают и начинают светиться (RuinsIncomeGlow), а медальон перерождается:
 * сжимается и раскаляется добела, рассыпается вспышкой, и из нее вырастает новый,
 * крупнее прежнего, с новым доходом. Постояв, все гаснет, и камера возвращается к
 * кадру игрока.
 *
 * Запускает ее middleware (ruinsIncomeAnimationMiddleware). Как и у облета
 * захваченных городов: панели погашены, поле не двигается и не кликается
 * (isFieldInputLocked в GameScene), снабжение на поле и оверлей «Ваш ход» ждут
 * конца. Времени хода сервер на нее не добавляет: снабжение идет параллельно, и
 * боты ее не ждут. Флаг гасит только этот класс — в конце, при обрыве и по сторожу.
 */
export class RuinsIncomeAnimation {
  constructor(scene, config = RUINS_INCOME_ANIMATION) {
    this.scene = scene;
    this.config = config;
    this.run = null;
  }

  get isActive() {
    return Boolean(this.run);
  }

  get isInputLocked() {
    return Boolean(this.run);
  }

  /**
   * Можно ли показывать: по тем же правилам, что облет захваченных городов (см.
   * CityCaptureAnimation.canPlay). Модалки в этом случае нет, руины просто
   * начинают светиться
   */
  canPlay(state) {
    const {scene} = this;
    if (!scene.isSceneActive || !scene.cameras?.main || !scene.hexGridRenderer) {
      return false;
    }
    if (selectGameType(state) === GAME_TYPES.ONLINE && checkIfPageHidden()) {
      return false;
    }
    if (selectGameType(state) === GAME_TYPES.REPLAY) {
      return !checkIfReplayRewinding()
        && getReplayPlaybackSpeedMultiplier() <= FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER;
    }
    if (scene.isDemoMode) {
      return BOT_ACTION_INTERVAL_MS / getBotActionIntervalMs() <= FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER;
    }
    return true;
  }

  /** @returns {boolean} запущена ли анимация; нет — руины просто начнут светиться */
  start() {
    if (this.run) {
      return false;
    }
    const {scene} = this;
    const state = scene.store.getState();
    if (!this.canPlay(state)) {
      return false;
    }
    const renderer = scene.hexGridRenderer;
    const changes = buildRuinsIncomeChanges(state).filter(({hexKey}) => renderer.hexes[hexKey]);
    const playArea = scene.calcPlayAreaBounds?.(state);
    if (changes.length === 0 || !playArea) {
      return false;
    }

    const run = {
      changes,
      playArea,
      pending: new Set(),
      objects: new Set(),
      masks: new Set(),
      loops: new Set(),
      isCancelled: false,
      isFinished: false,
      watchdog: null,
      savedCamera: null,
      onVisibilityChange: null,
      strikeSounds: {count: 0, lastAtMs: -Infinity},
    };
    this.run = run;
    renderer.resetRuinsIncomeGlowReveal?.();
    scene.store.dispatch(startRuinsIncomeAnimation());
    const maxDurationMs = this.getMaxDurationMs();
    markAwaitedAnimationPlaying(AWAITED_ANIMATION_KEY, Date.now() + maxDurationMs);
    this.armWatchdog(run, maxDurationMs);
    this.watchNetworkWindow(run, state);

    this.play(run).catch((error) => {
      if (error === CANCELLED) {
        return;
      }
      console.warn('[RuinsIncome] анимация дохода руин оборвалась', error);
      this.abort({restoreCamera: true});
    });
    return true;
  }

  async play(run) {
    const {scene, config} = this;
    scene.stopCameraPan?.();
    // Облет городов еще везет камеру назад (игрок быстро закрыл итоги, модалки
    // скрыты): кадр игрока берем у него — нечего ехать туда и обратно
    run.savedCamera = scene.cityCaptureAnimation?.handOverCamera?.() || scene.getCameraFrame();

    await this.settle(run);

    const camera = scene.cameras.main;
    const platePx = this.getPlatePx();
    const frame = this.getCameraFrame(run, platePx);
    const layout = this.buildLayout(run, frame, platePx);
    const dim = this.createDim(run, frame);
    this.playSound(config.sounds.riser);
    const from = scene.getCameraFrame();
    await this.runTween(run, config.cameraMoveMs, (t) => {
      const next = interpolateCameraFrame(from, frame, easeInOutSine(t));
      camera.setZoom(next.zoom);
      scene.centerOnFreeArea(next.x, next.y);
      dim.setAlpha(config.dim.alpha * t);
    });

    const badges = layout.points.map((point) => this.createBadge(run, point, layout));
    await this.revealBadges(run, badges, layout);
    await this.runTween(run, config.beforeWaveHoldMs);

    await this.playStrikes(run, badges, layout);
    await this.runTween(run, config.afterWaveHoldMs);

    await this.runTween(run, config.outroMs, (t) => {
      badges.forEach(({container, plateScale}) => {
        container.setAlpha(1 - t);
        container.setScale(plateScale * (1 - 0.18 * t));
      });
      dim.setAlpha(config.dim.alpha * (1 - t));
    });
    this.stopLoops(run);
    this.destroyObjects(run, [...run.objects]);

    const restoreFrom = scene.getCameraFrame();
    await this.runTween(run, config.restoreCameraMs, (t) => {
      const next = interpolateCameraFrame(restoreFrom, run.savedCamera, easeInOutSine(t));
      camera.setZoom(next.zoom);
      scene.centerOnFreeArea(next.x, next.y);
    });
    this.finish(run);
    this.cleanup(run);
  }

  getCanvasPxPerCssPx() {
    const {scene} = this;
    return scene.scale?.zoom ? 1 / scene.scale.zoom : 1;
  }

  /**
   * Что видно на время анимации: футер погашен, поэтому его край тоже отдан полю.
   * bottomInsetPx — его высота в пикселях канвы: сцена по-прежнему ставит кадры
   * относительно области без него (centerOnFreeArea)
   */
  getVisibleArea() {
    const {scene} = this;
    const freeAreaPx = scene.getFreeAreaSizePx();
    const bottomInsetPx = (scene.fieldInsets?.bottom || 0) * this.getCanvasPxPerCssPx();
    return {width: freeAreaPx.width, height: freeAreaPx.height + bottomInsetPx, bottomInsetPx};
  }

  /** Сторона медальона на экране, в пикселях канвы */
  getPlatePx() {
    return calcRuinsIncomePlateSizePx(this.getVisibleArea(), this.getCanvasPxPerCssPx(), this.config.plate);
  }

  /** Кадр камеры: все поле по вертикали и над ним — место для верхних медальонов */
  getCameraFrame(run, platePx) {
    const {scene, config} = this;
    const ruinHexes = run.changes.map(({hexKey}) => scene.hexGridRenderer.hexes[hexKey]);
    const area = this.getVisibleArea();
    const topReservePx = platePx * config.plate.visibleShare * config.rebirth.finalScale;
    const frame = calcRuinsIncomeCameraFrame(run.playArea, ruinHexes, area, config, topReservePx);
    return {...frame, y: frame.y - area.bottomInsetPx / 2 / frame.zoom};
  }

  /**
   * Где и какого размера стоят медальоны и когда до них дойдет волна. Медальон
   * держит размер на экране, поэтому на поле он тем больше, чем дальше камера.
   * Место считается под переродившийся медальон: он крупнее прежнего
   */
  buildLayout(run, frame, platePx) {
    const {scene, config} = this;
    const renderer = scene.hexGridRenderer;
    const {plate, badge, rebirth} = config;
    const area = this.getVisibleArea();
    const hexes = run.changes.map(({hexKey}) => renderer.hexes[hexKey]);
    const hexSize = hexes[0].size;
    const plateWorldSize = platePx / frame.zoom;
    const medallionSize = plateWorldSize * plate.visibleShare * rebirth.finalScale;
    const halfWidth = area.width / frame.zoom / 2;
    const halfHeight = area.height / frame.zoom / 2;
    const viewCenterY = frame.y + area.bottomInsetPx / 2 / frame.zoom;

    const positions = placeRuinsIncomeMedallions(hexes, medallionSize, {
      minX: frame.x - halfWidth + medallionSize / 2,
      maxX: frame.x + halfWidth - medallionSize / 2,
      minY: viewCenterY - halfHeight + medallionSize / 2,
      maxY: viewCenterY + halfHeight - medallionSize / 2,
    }, badge);
    const points = run.changes.map((change, index) => ({...change, hex: hexes[index], ...positions[index]}));

    const origin = {x: run.playArea.centerX, y: run.playArea.centerY};
    const {delays, maxDistance} = calcRuinsIncomeWaveDelays(
      hexes.map((hex, index) => ({hexKey: run.changes[index].hexKey, x: hex.centerX, y: hex.centerY})),
      origin,
      config.waveSpreadMs
    );
    return {
      points,
      origin,
      delays,
      maxDistance,
      hexSize,
      medallionSize,
      // Сторона медальона в пикселях на поле при масштабе контейнера 1
      plateBaseSize: hexSize * INCOME_MARKER.plateSizeFactor,
      plateWorldSize,
    };
  }

  /** Поле темное, а ячейки руин светлые: затемнение с дырками по ним */
  createDim(run, frame) {
    const {scene, config} = this;
    const renderer = scene.hexGridRenderer;
    const freeAreaPx = scene.getFreeAreaSizePx();
    const camera = scene.cameras.main;
    const width = Math.max(camera.width, freeAreaPx.width) / frame.zoom * (1 + config.dim.viewPaddingShare);
    const height = Math.max(camera.height, freeAreaPx.height) / frame.zoom * (1 + config.dim.viewPaddingShare);
    const dim = this.track(run, scene.add.graphics());
    dim.fillStyle(config.dim.color, 1);
    dim.fillRect(frame.x - width, frame.y - height, width * 2, height * 2);
    dim.setDepth(DIM_DEPTH);
    dim.setAlpha(0);

    const holes = this.track(run, scene.make.graphics({add: false}));
    holes.fillStyle(0xffffff, 1);
    run.changes.forEach(({hexKey}) => {
      renderer.fillHexShape?.(holes, renderer.hexes[hexKey], 0);
    });
    const mask = holes.createGeometryMask();
    mask.setInvertAlpha(true);
    run.masks.add(mask);
    dim.setMask(mask);
    return dim;
  }

  /**
   * Медальон у руин. Прежний доход виден сразу, новый ждет перерождения. Рисует их
   * общий код плашки дохода: это те же медальоны, что на поле. Поверх — белый силуэт
   * той же формы: им медальон раскаляется и им же рассыпается
   */
  createBadge(run, point, layout) {
    const {scene} = this;
    const {hexSize, plateBaseSize, plateWorldSize} = layout;
    const plateScale = plateWorldSize / plateBaseSize;

    const createMedallion = (value, hasHalo) => {
      const {elements} = createIncomeMarker(scene, {
        x: 0,
        y: 0,
        hexSize,
        value,
        incomeType: INCOME_TYPE.WIN_POINTS,
        isBlocked: point.isBlocked,
        hasHalo,
        depth: BADGE_DEPTH,
        textStyle: value >= TWO_DIGITS_FROM ? {fontSize: TWO_DIGITS_FONT_SIZE} : {},
      });
      return scene.add.container(0, 0, elements);
    };

    const oldMedallion = createMedallion(point.before, false);
    const newMedallion = createMedallion(point.after, true);
    newMedallion.setVisible(false);
    const white = scene.add.image(0, 0, INCOME_MARKER.themes[INCOME_TYPE.WIN_POINTS].plateKey);
    white.setTintFill(WHITE);
    const whiteScale = plateBaseSize / (white.width || 1);
    white.setScale(whiteScale);
    white.setAlpha(0);

    const container = this.track(run, scene.add.container(point.x, point.y, [oldMedallion, newMedallion, white]));
    container.setDepth(BADGE_DEPTH);
    container.setScale(0);
    return {point, container, oldMedallion, newMedallion, white, whiteScale, plateScale};
  }

  /** Медальоны «было» выпрыгивают волной от центра поля */
  async revealBadges(run, badges, layout) {
    const {config} = this;
    const {delays} = layout;
    const maxDelay = Math.max(1, config.waveSpreadMs);
    await Promise.all(badges.map(({point, container, plateScale}) => (
      this.runTween(run, delays[point.hexKey] / maxDelay * config.revealSpreadMs)
        .then(() => this.runObjectTween(run, {
          targets: container,
          scaleX: plateScale,
          scaleY: plateScale,
          duration: config.popMs,
          ease: POP_EASE,
        }))
    )));
  }

  /** Волна света от центра поля до самых дальних руин */
  playWave(layout) {
    const {scene, config} = this;
    const {origin, maxDistance} = layout;
    this.playCast(buildRuinsIncomeWaveCastPlan({
      center: origin,
      radius: maxDistance * (1 + config.waveTailMs / Math.max(1, config.waveSpreadMs)),
      durationMs: config.waveSpreadMs + config.waveTailMs,
      hexRotation: scene.hexGridRenderer.getHexRotation?.(scene.hexGridRenderer.hexes[layout.points[0].hexKey]) || 0,
    }));
  }

  /**
   * Волна доходит до руин. Столб света сходит чуть раньше нее, медальон в это время
   * копит силу; на самой волне вспышка, руины начинают светиться, и медальон
   * перерождается. Волна стартует позже на то же время, чтобы и центральным руинам
   * его хватило
   */
  async playStrikes(run, badges, layout) {
    const {config} = this;
    const {delays} = layout;
    const lead = config.strike.beamLeadMs;
    const latest = Math.max(...badges.map(({point}) => delays[point.hexKey]));
    const particleShare = Math.max(
      config.minParticleShare,
      Math.min(1, config.particleBudget / (badges.length * config.particlesPerRuin))
    );
    let isFirstStrike = true;
    await Promise.all([
      this.runTween(run, lead).then(() => this.playWave(layout)),
      ...badges.map(async (badge) => {
        const delay = delays[badge.point.hexKey];
        await this.runTween(run, delay);
        this.playBadgeCasts(badge, layout, particleShare);
        await this.chargeBadge(run, badge, layout);

        this.scene.hexGridRenderer.revealRuinsIncomeGlow?.(badge.point.hexKey);
        if (isFirstStrike) {
          isFirstStrike = false;
          this.playSound(config.sounds.impact);
          shakeFieldCamera(this.scene, config.shake);
        } else {
          this.playStrikeSound(run, delay);
        }
        if (delay === latest && badges.length > 1) {
          shakeFieldCamera(this.scene, config.shake);
        }
        await this.rebirthBadge(run, badge);
      }),
    ]);
  }

  /** Столб света на руинах и вспышка, лучи и осколки вокруг медальона */
  playBadgeCasts({point}, layout, particleShare) {
    const hexRotation = this.scene.hexGridRenderer.getHexRotation?.(point.hex) || 0;
    this.playCast(buildRuinsIncomeStrikeCastPlan({
      center: {x: point.hex.centerX, y: point.hex.centerY},
      size: Math.max(point.hex.size, layout.plateWorldSize * EFFECT_SIZE_SHARE_OF_PLATE),
      isSuperRuins: checkIfSuperRuins(point.tokenType),
      particleShare,
      hexRotation,
    }));
    this.playCast(buildRuinsIncomeRebirthCastPlan({
      center: {x: point.x, y: point.y},
      size: layout.medallionSize,
      isSuperRuins: checkIfSuperRuins(point.tokenType),
      particleShare,
      hexRotation,
    }));
  }

  /** Медальон копит силу: сжимается, дрожит все сильнее и раскаляется добела */
  chargeBadge(run, {oldMedallion, white, whiteScale}, {plateBaseSize}) {
    const {rebirth, strike} = this.config;
    const amplitude = plateBaseSize * rebirth.chargeShakeShare;
    return this.runTween(run, strike.beamLeadMs, (t) => {
      const scale = 1 + (rebirth.chargeScale - 1) * t * t;
      oldMedallion.setScale(scale);
      oldMedallion.setX(Math.sin(t * 40) * amplitude * t);
      white.setScale(whiteScale * scale);
      white.setX(oldMedallion.x);
      white.setAlpha(rebirth.chargeWhiteAlpha * t * t);
    });
  }

  /**
   * Прежний медальон рассыпается вспышкой, из нее вырастает новый с перелетом и
   * остается крупнее прежнего. Дальше он мерно дышит, пока итог на виду
   */
  async rebirthBadge(run, {oldMedallion, newMedallion, white, whiteScale}) {
    const {rebirth} = this.config;
    oldMedallion.setVisible(false);
    white.setX(0);
    white.setAlpha(1);
    newMedallion.setVisible(true);
    newMedallion.setScale(rebirth.fromScale);
    newMedallion.setAlpha(0);
    await Promise.all([
      this.runObjectTween(run, {
        targets: white,
        scaleX: whiteScale * rebirth.flareScale,
        scaleY: whiteScale * rebirth.flareScale,
        alpha: 0,
        duration: rebirth.flareMs,
        ease: GROW_EASE,
      }),
      this.runObjectTween(run, {
        targets: newMedallion,
        scaleX: rebirth.peakScale,
        scaleY: rebirth.peakScale,
        alpha: 1,
        duration: rebirth.growMs,
        ease: GROW_EASE,
      }).then(() => this.runObjectTween(run, {
        targets: newMedallion,
        scaleX: rebirth.finalScale,
        scaleY: rebirth.finalScale,
        duration: rebirth.settleMs,
        ease: SETTLE_EASE,
      })),
    ]);
    this.startBreathing(run, newMedallion);
  }

  startBreathing(run, medallion) {
    const {rebirth} = this.config;
    const scale = rebirth.finalScale * rebirth.breathScale;
    const loop = this.scene.tweens.add({
      targets: medallion,
      scaleX: scale,
      scaleY: scale,
      duration: rebirth.breathMs,
      yoyo: true,
      repeat: -1,
      ease: SETTLE_EASE,
    });
    run.loops.add(loop);
  }

  stopLoops(run) {
    run.loops.forEach((loop) => loop?.stop?.());
    run.loops.clear();
  }

  /**
   * Колокольчик на следующих руинах: удары волны близко друг к другу сливались
   * бы в треск, поэтому не чаще промежутка и не больше предела
   */
  playStrikeSound(run, atMs) {
    const {strike, strikeSoundGapMs, strikeSoundLimit} = this.config.sounds;
    const sounds = run.strikeSounds;
    if (sounds.count >= strikeSoundLimit || atMs - sounds.lastAtMs < strikeSoundGapMs) {
      return;
    }
    sounds.count += 1;
    sounds.lastAtMs = atMs;
    this.playSound(strike);
  }

  playSound(soundId) {
    try {
      this.scene.soundManager?.playDeferredSound?.(soundId);
    } catch (error) {
      console.warn('[RuinsIncome] не удалось проиграть звук', error);
    }
  }

  playCast(plan) {
    try {
      this.scene.hexGridRenderer.abilityCastAnimation?.play(plan);
    } catch (error) {
      console.warn('[RuinsIncome] не удалось показать вспышку', error);
    }
  }

  track(run, object) {
    run.objects.add(object);
    return object;
  }

  async settle(run) {
    await this.runTween(run, this.config.settleMs);
    await this.runTween(run, 1);
  }

  /**
   * Твин-счетчик от 0 до 1 за `durationMs`; без `onUpdate` — просто пауза.
   * Обрыв отклоняет ожидание (CANCELLED)
   */
  runTween(run, durationMs, onUpdate) {
    return this.runTracked(run, (onComplete) => this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: Math.max(1, durationMs),
      ease: LINEAR_EASE,
      onUpdate: (counter) => {
        if (!run.isCancelled) {
          onUpdate?.(counter.getValue());
        }
      },
      onComplete: () => {
        if (!run.isCancelled) {
          onUpdate?.(1);
        }
        onComplete();
      },
    }));
  }

  /** Твин объекта с тем же обрывом, что у счетчика */
  runObjectTween(run, config) {
    return this.runTracked(run, (onComplete) => this.scene.tweens.add({...config, onComplete}));
  }

  runTracked(run, createTween) {
    return new Promise((resolve, reject) => {
      if (run.isCancelled) {
        reject(CANCELLED);
        return;
      }
      const entry = {reject, stop: null};
      const tween = createTween(() => {
        run.pending.delete(entry);
        if (!run.isCancelled) {
          resolve();
        }
      });
      entry.stop = () => tween?.stop?.();
      run.pending.add(entry);
    });
  }

  getMaxDurationMs() {
    const {watchdogSlowdownFactor, watchdogPaddingMs} = this.config;
    return getRuinsIncomeAnimationDurationMs(this.config) * watchdogSlowdownFactor + watchdogPaddingMs;
  }

  armWatchdog(run, timeoutMs) {
    run.watchdog = setTimeout(() => {
      if (this.run === run) {
        console.warn('[RuinsIncome] анимация не уложилась во время — обрываем');
        this.abort({restoreCamera: true});
      }
    }, timeoutMs);
  }

  /**
   * Сетевая партия: вкладку скрыли посреди анимации, а вернули, когда она давно
   * должна была кончиться, — обрываем и возвращаем кадр игрока: идет уже чужой
   * или его собственный ход, поле ему нужно сразу
   */
  watchNetworkWindow(run, state) {
    if (selectGameType(state) !== GAME_TYPES.ONLINE || typeof document === 'undefined') {
      return;
    }
    const windowEndsAt = Date.now() + getRuinsIncomeAnimationDurationMs(this.config);
    run.onVisibilityChange = () => {
      if (this.run === run && !checkIfPageHidden() && Date.now() >= windowEndsAt) {
        this.abort({restoreCamera: true});
      }
    };
    document.addEventListener('visibilitychange', run.onVisibilityChange);
  }

  /** Снять флаг: снабжение, оверлей хода, панели и цвет фазы возвращаются */
  finish(run) {
    if (run.isFinished) {
      return;
    }
    run.isFinished = true;
    this.scene.store.dispatch(finishRuinsIncomeAnimation());
  }

  destroyObjects(run, objects) {
    objects.forEach((object) => {
      run.objects.delete(object);
      try {
        object.clearMask?.(false);
        object.destroy?.();
      } catch (error) {
        // Объект уже разобран вместе со сценой
      }
    });
  }

  cleanup(run) {
    clearTimeout(run.watchdog);
    if (run.onVisibilityChange) {
      document.removeEventListener('visibilitychange', run.onVisibilityChange);
      run.onVisibilityChange = null;
    }
    markAwaitedAnimationFinished(AWAITED_ANIMATION_KEY);
    this.stopLoops(run);
    this.destroyObjects(run, [...run.objects]);
    run.masks.forEach((mask) => {
      try {
        mask.destroy?.();
      } catch (error) {
        // Маска уже разобрана вместе со сценой
      }
    });
    run.masks.clear();
    if (this.run === run) {
      this.run = null;
    }
  }

  /**
   * Оборвать: убрать слои, снять флаг. `restoreCamera` — сразу вернуть кадр
   * игрока (сцена жива, а анимация сорвалась); при разборе сцены камеру
   * возвращать некуда
   */
  abort({restoreCamera = false} = {}) {
    const run = this.run;
    if (!run) {
      return;
    }
    run.isCancelled = true;
    run.pending.forEach((entry) => {
      entry.stop?.();
      entry.reject(CANCELLED);
    });
    run.pending.clear();
    if (restoreCamera && run.savedCamera && this.scene.cameras?.main) {
      this.scene.cameras.main.setZoom(run.savedCamera.zoom);
      this.scene.centerOnFreeArea(run.savedCamera.x, run.savedCamera.y);
    }
    this.cleanup(run);
    this.finish(run);
  }
}
